import { SubscriptionFeature } from './subscription.js'

export type PrivateAuthorisation =
	| 'getCSRFToken'
	| 'authentify'
	| 'authentified'
	| 'scheduleTypes'
	| 'absenceTypes'
	| 'expenseTypes'
	| 'customFields'

export type PublicAuthorisation =
	| 'admin'
	| 'toTag'
	| 'assignPeopleManager'
	| 'assignProjectManager'
	| 'assignTaskManager'
	| 'assignTaskOwner'
	| 'assignTagManager'
	| 'assignProjects'
	| 'assignTasks'
	| 'assignAbsenceTypes'
	| 'assignExpenseTypes'
	| 'assignCustomFields'
	| 'assignScheduleTypes'
	| 'organisation'
	| 'subscription'
	| 'editPersonRoles'
	| 'tags'
	| 'persons'
	| 'projects'
	| 'tasks'
	| 'editScheduleTypes'
	| 'editAbsenceTypes'
	| 'editExpenseTypes'
	| 'editCustomFields'
	| 'expenseRecords'
	| 'timeRecords'
	| 'localisation'
	| 'notificationSettings'
	| 'emailTemplates'
	| 'timeSettings'
	| 'holidays'
	| 'absenceQuotas'
	| 'billings'
	| 'costs'
	| 'budgets'
	| 'utilization'
	| 'timesheetCompliance'
	| 'messages'
	| 'customFieldValues'
	| 'approvalStages'
	| 'defaultAvailability'
	| 'description'
	| 'sso'
	| 'user'
	| 'reports'
	| 'projectsAllowed'
	| 'schedule'
	| 'validityPeriod'

export type Target =
	| 'me'
	| 'managedPeople'
	| 'managedProjects'
	| 'managedTasks'
	| 'ownedTasks'
	| 'managedProjectTasks'
	| 'managedPeopleTasks'
	| 'managedTags'
	| 'managedProjectMembers'
	| 'peopleColleagues'
	| 'projectColleagues'
	| 'taskColleagues'
	| 'assignedProjects'
	| 'assignedTasks'
	| 'assignedScheduleTypes'
	| 'assignedAbsenceTypes'
	| 'assignedExpenseTypes'
	| 'assignedCustomFields'
	| 'organisation'

export const peopleTargets: Target[] = [
	'me',
	'managedPeople',
	'managedProjectMembers',
	'peopleColleagues',
	'projectColleagues',
	'taskColleagues',
]

export const projectTargets: Target[] = ['managedProjects', 'assignedProjects']
export const taskTargets: Target[] = [
	'managedTasks',
	'ownedTasks',
	'assignedTasks',
	'managedProjectTasks',
	'managedPeopleTasks',
]
export const tagTargets: Target[] = ['managedTags']
export const organisationTargets: Target[] = ['organisation']

export enum auths {
	Admin = 'admin',
	ToTag = 'toTag',
	AssignPeopleManager = 'assignPeopleManager',
	AssignProjectManager = 'assignProjectManager',
	AssignTaskManager = 'assignTaskManager',
	AssignTaskOwner = 'assignTaskOwner',
	AssignTagManager = 'assignTagManager',
	AssignProjects = 'assignProjects',
	AssignTasks = 'assignTasks',
	AssignAbsenceTypes = 'assignAbsenceTypes',
	AssignExpenseTypes = 'assignExpenseTypes',
	AssignCustomFields = 'assignCustomFields',
	AssignScheduleTypes = 'assignScheduleTypes',
	Organisation = 'organisation',
	Subscription = 'subscription',
	EditPersonRoles = 'editPersonRoles',
	Tag = 'tags',
	Person = 'persons',
	Project = 'projects',
	Task = 'tasks',
	EditScheduleTypes = 'editScheduleTypes',
	EditAbsenceTypes = 'editAbsenceTypes',
	EditExpenseTypes = 'editExpenseTypes',
	EditCustomFields = 'editCustomFields',
	ScheduleType = 'scheduleTypes',
	AbsenceType = 'absenceTypes',
	AbsenceRecord = 'absenceRecords',
	ExpenseType = 'expenseTypes',
	ExpenseRecord = 'expenseRecords',
	TimeRecords = 'timeRecords',
	Localisation = 'localisation',
	TimeSettings = 'timeSettings',
	Holidays = 'holidays',
	AbsenceQuota = 'absenceQuotas',
	Billing = 'billings',
	Cost = 'costs',
	Budget = 'budgets',
	Utilization = 'utilization',
	TimesheetCompliance = 'timesheetCompliance',
	Messages = 'messages',
	CustomField = 'customFields',
	ApprovalStages = 'approvalStages',
	DefaultAvailability = 'defaultAvailability',
	Description = 'description',
	NotificationSettings = 'notificationSettings',
	EmailTemplates = 'emailTemplates',
	SSO = 'sso',
	User = 'user',
	Reports = 'reports',
	ProjectsAllowed = 'projectsAllowed',
	CustomFieldValues = 'customFieldValues',
	Schedule = 'schedule',
	ValidityPeriod = 'validityPeriod',
}

// Authorisations kept as scopes (admins get them via the dynamic grant, and some still
// gate UI through hasAuth) but hidden from the role editor: they're admin-only settings,
// not meant to be delegated to custom roles, so they shouldn't appear as configurable rows.
export const hiddenAuthorisations: auths[] = [auths.Organisation]

// Maps each auth to the subscription feature that gates it (if any).
// An auth not in this map is purely role-based — no plan dependency.
// When the auth IS feature-gated and the plan doesn't include the feature,
// even admins are treated as not having it (so they see the upgrade UX).
export const featureOfAuth: Partial<Record<auths, SubscriptionFeature>> = {
	[auths.AbsenceQuota]: SubscriptionFeature.quotas,
	[auths.ApprovalStages]: SubscriptionFeature.approval,
	[auths.Billing]: SubscriptionFeature.billing,
	[auths.Budget]: SubscriptionFeature.budgets,
	[auths.Utilization]: SubscriptionFeature.budgets,
	[auths.Cost]: SubscriptionFeature.costs,
	[auths.CustomField]: SubscriptionFeature.customFields,
	[auths.EditCustomFields]: SubscriptionFeature.customFields,
	[auths.EditExpenseTypes]: SubscriptionFeature.expenses,
	[auths.ExpenseRecord]: SubscriptionFeature.expenses,
	[auths.ExpenseType]: SubscriptionFeature.expenses,
	[auths.SSO]: SubscriptionFeature.ssoCustom,
}
